import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatMoney } from '@/lib/api';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { FileText, Calculator, Info, AlertTriangle, ShieldCheck, Building2, UserX, CheckCircle2, Clock, ExternalLink } from 'lucide-react';

const profileIcons = { fisica: ShieldCheck, moral: Building2, sin_rfc: UserX };
const profileColors = {
    fisica: 'border-emerald-200 bg-emerald-50/60',
    moral: 'border-blue-200 bg-blue-50/60',
    sin_rfc: 'border-red-200 bg-red-50/60',
};

const REGIME_LABELS = { fisica: 'Persona Física (con RFC)', moral: 'Persona Moral (con RFC)', sin_rfc: 'Sin RFC registrado' };

export default function DashboardImpuestos() {
    const nav = useNavigate();
    const [cfg, setCfg] = useState(null);
    const [fiscalProfile, setFiscalProfile] = useState(null);

    // Quoter state
    const [amount, setAmount] = useState('1000');
    const [profile, setProfile] = useState('fisica');
    const [isGuanajuato, setIsGuanajuato] = useState(false);

    useEffect(() => {
        Promise.all([
            api.get('/taxes').catch(() => ({ data: null })),
            api.get('/me/fiscal-profile').catch(() => ({ data: null })),
        ]).then(([taxRes, fiscalRes]) => {
            if (taxRes.data) setCfg(taxRes.data);
            if (fiscalRes.data) {
                setFiscalProfile(fiscalRes.data);
                setProfile(fiscalRes.data.fiscal_regime || 'fisica');
            }
        });
    }, []);

    const selectedRate = useMemo(() => {
        if (!cfg) return { isr_pct: 2.5, iva_pct: 8.0 };
        return cfg.rates?.find(r => r.profile === profile) || { isr_pct: 0, iva_pct: 0 };
    }, [cfg, profile]);

    const guanajuatoExtra = useMemo(() => {
        if (!isGuanajuato || !cfg) return 0;
        return cfg.guanajuato_extra_pct || 0;
    }, [isGuanajuato, cfg]);

    const numAmount = parseFloat(amount) || 0;
    const isr = Math.round(numAmount * (selectedRate.isr_pct || 0) / 100 * 100) / 100;
    const iva = Math.round(numAmount * (selectedRate.iva_pct || 0) / 100 * 100) / 100;
    const extra = Math.round(numAmount * guanajuatoExtra / 100 * 100) / 100;
    const totalRetained = isr + iva + extra;
    const netIncome = Math.max(0, numAmount - totalRetained);
    const retentionPct = numAmount > 0 ? Math.round((totalRetained / numAmount) * 100 * 10) / 10 : 0;

    if (!cfg) return <div className="text-sm text-muted-foreground py-8">Cargando…</div>;

    return (
        <div className="space-y-6 max-w-3xl">
            <div>
                <h1 className="text-2xl font-semibold tracking-tight">Impuestos</h1>
                <p className="text-sm text-muted-foreground">Información sobre retenciones fiscales aplicadas a tus ventas según la Ley de Plataformas Digitales del SAT.</p>
            </div>

            {/* Fiscal profile status card */}
            {fiscalProfile && (
                <div className={`rounded-2xl border p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 ${fiscalProfile.fiscal_profile_active ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/40'}`}>
                    <div className="flex items-center gap-3">
                        {fiscalProfile.fiscal_profile_active ? (
                            <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
                        ) : (
                            <Clock className="h-6 w-6 text-amber-600 shrink-0" />
                        )}
                        <div>
                            <div className="font-semibold text-sm">
                                {fiscalProfile.fiscal_profile_active ? 'Perfil fiscal activo ✓' : 'Perfil fiscal inactivo — tasas máximas aplicadas'}
                            </div>
                            <div className="text-xs text-muted-foreground">
                                Régimen: {REGIME_LABELS[fiscalProfile.fiscal_regime] || 'Sin RFC'}
                                {fiscalProfile.tax_id ? ` · RFC: ${fiscalProfile.tax_id}` : ''}
                            </div>
                        </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => nav('/dashboard/kyc#fiscal')} className="shrink-0 text-xs">
                        <ExternalLink className="h-3.5 w-3.5 mr-1" />Actualizar perfil fiscal
                    </Button>
                </div>
            )}

            {/* Legal info banner */}
            <div className="rounded-2xl overflow-hidden">
                <div className="px-5 py-3 bg-gradient-to-r from-slate-900 to-neutral-800 text-white text-sm font-semibold flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    ¿Por qué me retienen impuestos?
                </div>
                <div className="border border-t-0 rounded-b-2xl p-4 text-sm text-muted-foreground space-y-2">
                    <p>
                        Conforme al <strong>artículo 18-J de la LISR</strong> y los artículos de la LIVA, las plataformas digitales como Ok te lo Vendo están obligadas por ley a <strong>retener y enterar</strong> ISR e IVA en nombre de sus vendedores ante el SAT.
                    </p>
                    <p>
                        Las retenciones se aplican sobre el <strong>ingreso bruto</strong> de cada venta y se reportan mensualmente al SAT. Los vendedores pueden acreditar estas retenciones en su declaración anual.
                    </p>
                    <p>
                        Si tienes tu RFC registrado en tu perfil, aplican las tasas reducidas. Sin RFC, se aplican tasas de penalización máximas (ISR 20% + IVA 16%).
                    </p>
                </div>
            </div>

            {/* Rate cards */}
            <div className="space-y-3">
                <h2 className="font-semibold text-sm">Tasas de retención vigentes</h2>
                {(cfg.rates || []).map(rate => {
                    const Icon = profileIcons[rate.profile] || ShieldCheck;
                    return (
                        <div key={rate.profile} className={`rounded-2xl border p-4 ${profileColors[rate.profile] || ''}`}>
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <Icon className={`h-4 w-4 ${rate.profile === 'sin_rfc' ? 'text-red-600' : rate.profile === 'moral' ? 'text-blue-600' : 'text-emerald-600'}`} />
                                    <span className="font-semibold text-sm">{rate.label}</span>
                                </div>
                                <Badge variant={rate.profile === 'sin_rfc' ? 'destructive' : 'outline'} className="text-[11px]">
                                    {(rate.isr_pct + rate.iva_pct).toFixed(1)}% total
                                </Badge>
                            </div>
                            <div className="grid grid-cols-3 gap-3 text-center">
                                <div className="rounded-lg bg-white/70 p-2">
                                    <div className="text-lg font-bold tabular-nums">{rate.isr_pct}%</div>
                                    <div className="text-[11px] text-muted-foreground">ISR</div>
                                </div>
                                <div className="rounded-lg bg-white/70 p-2">
                                    <div className="text-lg font-bold tabular-nums">{rate.iva_pct}%</div>
                                    <div className="text-[11px] text-muted-foreground">IVA</div>
                                </div>
                                <div className="rounded-lg bg-white/70 p-2">
                                    <div className={`text-lg font-bold tabular-nums ${rate.profile === 'sin_rfc' ? 'text-red-600' : 'text-emerald-700'}`}>
                                        {(100 - rate.isr_pct - rate.iva_pct).toFixed(1)}%
                                    </div>
                                    <div className="text-[11px] text-muted-foreground">Recibes</div>
                                </div>
                            </div>
                        </div>
                    );
                })}

                {/* Guanajuato */}
                {cfg.guanajuato_extra_pct > 0 && (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
                        <div className="flex items-center gap-2 mb-1">
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                            <span className="font-semibold text-sm">Impuesto Cedular SATEG — Guanajuato</span>
                            <Badge variant="outline" className="border-amber-500 text-amber-700 text-[11px]">{cfg.guanajuato_extra_pct}% extra</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">Si tu dirección de vendedor está registrada en el estado de Guanajuato, se aplica un descuento adicional de {cfg.guanajuato_extra_pct}% por el Impuesto Cedular del SATEG sobre tu ingreso bruto.</p>
                    </div>
                )}
            </div>

            {/* Interactive quoter */}
            <div className="rounded-2xl border bg-card overflow-hidden">
                <div className="px-5 py-4 border-b bg-gradient-to-r from-slate-900 to-neutral-800 text-white flex items-center gap-2">
                    <Calculator className="h-5 w-5" />
                    <div>
                        <div className="font-semibold">Cotizador fiscal</div>
                        <div className="text-xs text-white/60">Simula cuánto se retendrá de tu venta</div>
                    </div>
                </div>
                <div className="p-5 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs">Monto de venta (MXN)</Label>
                            <Input type="number" min="1" value={amount}
                                onChange={e => setAmount(e.target.value)}
                                placeholder="Ej. 1000" className="mt-1" />
                        </div>
                        <div>
                            <Label className="text-xs">Régimen fiscal</Label>
                            <Select value={profile} onValueChange={setProfile}>
                                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="fisica">Persona Física (con RFC)</SelectItem>
                                    <SelectItem value="moral">Persona Moral / Empresa (con RFC)</SelectItem>
                                    <SelectItem value="sin_rfc">Sin RFC registrado</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {cfg.guanajuato_extra_pct > 0 && (
                        <div className="flex items-center gap-3">
                            <Switch checked={isGuanajuato} onCheckedChange={setIsGuanajuato} />
                            <Label className="text-sm cursor-pointer" onClick={() => setIsGuanajuato(!isGuanajuato)}>
                                Tengo dirección en <strong>Guanajuato</strong> (+ {cfg.guanajuato_extra_pct}% SATEG)
                            </Label>
                        </div>
                    )}

                    {/* Results */}
                    {numAmount > 0 && (
                        <div className="rounded-xl border divide-y text-sm">
                            <div className="px-4 py-2.5 flex justify-between">
                                <span className="text-muted-foreground">Ingreso bruto</span>
                                <span className="font-medium tabular-nums">{formatMoney(numAmount, 'MXN')}</span>
                            </div>
                            <div className="px-4 py-2.5 flex justify-between">
                                <span className="text-muted-foreground">ISR retenido ({selectedRate.isr_pct}%)</span>
                                <span className="tabular-nums text-red-600">− {formatMoney(isr, 'MXN')}</span>
                            </div>
                            <div className="px-4 py-2.5 flex justify-between">
                                <span className="text-muted-foreground">IVA retenido ({selectedRate.iva_pct}%)</span>
                                <span className="tabular-nums text-red-600">− {formatMoney(iva, 'MXN')}</span>
                            </div>
                            {extra > 0 && (
                                <div className="px-4 py-2.5 flex justify-between">
                                    <span className="text-muted-foreground">Impuesto Cedular SATEG ({cfg.guanajuato_extra_pct}%)</span>
                                    <span className="tabular-nums text-amber-600">− {formatMoney(extra, 'MXN')}</span>
                                </div>
                            )}
                            <div className="px-4 py-2.5 flex justify-between bg-red-50/60">
                                <span className="font-medium text-red-700">Total retenido ({retentionPct}%)</span>
                                <span className="font-semibold tabular-nums text-red-700">− {formatMoney(totalRetained, 'MXN')}</span>
                            </div>
                            <div className="px-4 py-3 flex justify-between bg-emerald-50 rounded-b-xl">
                                <span className="font-semibold text-emerald-700">Tú recibes neto</span>
                                <span className="font-bold text-emerald-700 text-base tabular-nums">{formatMoney(netIncome, 'MXN')}</span>
                            </div>
                        </div>
                    )}

                    <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                        <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                        Las retenciones son enteradas al SAT mensualmente por la plataforma. Puedes acreditarlas en tu declaración anual. Este cotizador es informativo; consulta a tu contador para asesoría específica.
                    </p>
                </div>
            </div>

            {/* SAT reference */}
            <div className="rounded-2xl border border-blue-200 bg-blue-50/40 p-4 text-sm">
                <div className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4" />Referencias legales (SAT)
                </div>
                <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                    <li>Ley del ISR, Artículo 18-J — Obligaciones de plataformas digitales</li>
                    <li>LIVA, Artículo 1-A Bis — Retención de IVA por plataformas</li>
                    <li>Regla Miscelánea Fiscal (RMF) — Sección 12.3 Plataformas digitales</li>
                    <li>Las retenciones aplican sobre el <em>ingreso bruto por operación</em></li>
                    <li>RFC registrado: ISR 2.5% + IVA 8% (50% del IVA general causado)</li>
                    <li>Sin RFC: ISR 20% + IVA 16% (tasas máximas de penalización)</li>
                    <li>Estado de Guanajuato: + {cfg.guanajuato_extra_pct}% Impuesto Cedular SATEG</li>
                </ul>
            </div>
        </div>
    );
}
